import { useEffect, useMemo } from 'react';

import type { Id } from '../../api/types';
import { useIzteachSessionStore } from '../../stores/izteach';
import { useQltSlotAttendanceStore, useQltTimetableStore } from '../../stores/qlt';
import { resolvePeriodCourseScope } from '../periodLabels';
import { rosterOf } from '../submissions/submissions';
import type { BoardContextValue } from '../useBoardContext';
import { examLoadKey } from './classExams';
import { buildExamResults, examCardView, groupExams, type ExamAttempt } from './examResults';
import { useExamResultStore } from './store';

const NO_ATTEMPTS: ExamAttempt[] = [];

export interface ClassScope {
  classId: Id;
  gradeId?: Id;
  subjectId?: Id;
}

/** The class's exam list (units and counts, no attempts); the store keeps each class it loaded. */
export function useExamListLoad(scope: ClassScope, enabled = true) {
  const rows = useQltSlotAttendanceStore((state) => state.rows);
  const signedIn = useIzteachSessionStore((state) => Boolean(state.accessToken));
  const codes = useMemo(() => rosterOf(rows).map((student) => student.code), [rows]);
  const input = { codes, classId: scope.classId, gradeId: scope.gradeId, subjectId: scope.subjectId };
  const key = examLoadKey(input, signedIn);
  // a roster or token arriving later loads again
  useEffect(() => { if (enabled && codes.length > 0) void useExamResultStore.getState().load(key, input); }, [enabled, key]);
  return { reload: () => void useExamResultStore.getState().load(key, input, true) };
}

/** The period's class: a board opened by URL takes grade and subject from the timetable. */
export function useClassExamLoad(context: Pick<BoardContextValue, 'classId' | 'gradeId' | 'subjectId' | 'date' | 'index'>) {
  const cells = useQltTimetableStore((state) => state.todayCells);
  const scope = resolvePeriodCourseScope({ gradeId: context.gradeId, subjectId: context.subjectId }, cells, context);
  const { reload } = useExamListLoad({ classId: context.classId, gradeId: scope.gradeId, subjectId: scope.subjectId });
  return { reload, scope };
}

/** One exam's class results, its questions and the Xếp hạng order a sheet steps through. */
export function useExamResultsView(examId: string) {
  const read = useExamResultStore((state) => state.attempts[examId]);
  const key = useExamResultStore((state) => state.key);
  const pattern = useExamResultStore((state) => state.pattern);
  const listError = useExamResultStore((state) => state.error);
  const entry = useExamResultStore((state) => state.questions[examId]);
  const rows = useQltSlotAttendanceStore((state) => state.rows);
  const rosterLoading = useQltSlotAttendanceStore((state) => state.loading);
  const roster = useMemo(() => rosterOf(rows), [rows]);
  const questions = entry ? entry.questions : [];

  useEffect(() => { void useExamResultStore.getState().loadQuestions(examId); }, [examId]);
  // only this exam's attempts, read when its card opens; a Tải lại drops them, so they are read again
  useEffect(() => { void useExamResultStore.getState().loadAttempts(examId); }, [examId, key, pattern, Boolean(read)]);

  // no pattern yet means no roster or no email domain, which the list's error names
  const attempts = read ? read.attempts : NO_ATTEMPTS;
  const loading = read ? read.loading : Boolean(pattern) || (rosterLoading && roster.length === 0);
  const loadError = read ? read.error : pattern ? null : listError;
  const view = useMemo(() => examCardView(groupExams(attempts, roster), examId, { loading, error: loadError }), [attempts, roster, examId, loading, loadError]);
  const exam = view.kind === 'ready' ? view.exam : null;
  const results = useMemo(() => (exam ? buildExamResults(exam, roster, questions) : null), [exam, roster, questions]);
  const order = useMemo<ExamAttempt[]>(() => (exam && results ? results.leaders.map((row) => exam.byCode[row.code]).filter(Boolean) : []), [exam, results]);
  const names = useMemo(() => {
    const out: Record<string, string> = {};
    roster.forEach((student) => { out[student.code] = student.name; });
    return out;
  }, [roster]);

  return {
    view,
    exam,
    results,
    order,
    names,
    questions,
    attemptsLoading: loading,
    questionsLoading: !entry || entry.loading,
    questionsError: entry ? entry.error : null,
    retryQuestions: () => void useExamResultStore.getState().loadQuestions(examId, true),
    retryAttempts: () => void useExamResultStore.getState().loadAttempts(examId, true),
  };
}
